import {
	GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult, NextPage,
} from 'next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import {
	useCallback, useEffect, useMemo,
} from 'react';
import { useMap } from 'react-map-gl';
import styled from 'styled-components';

import { fetchRoute } from '@/data/Route';
import Directions from '../../../components/map/Directions';
import { MapBox } from '../../../components/map/MapBox';
import { MapHeaderBackButton } from '../../../components/map/MapHeaderBackButton';
import { MapParcoursInfos } from '../../../components/map/parcours/MapParcoursInfos';
import { useSreenHeight } from '../../../custom/useScreenHeight';
import { useCapsulesFromRouteSlug } from '../../../data/Capsule.swr';
import { MapStore } from '../../../data/MapStore';
import { useMapBoxDirections } from '../../../data/Mapbox.swr';
import { useRoute } from '../../../data/Routes.swr';
import { DirectionProfiles } from '../../../models/MapBox';
import { NextPageProps } from '../../../typings/NextPageProps';
import { convertCapsulesToFeatures } from '../../../utils/geojson';
import { checkClient } from '../../../utils/serverSide';

const StyledContainer = styled.div<{screenHeight: number}>`
	display: flex;
	height: ${(props) => props.screenHeight}px;
	width: 100%;
	align-items: stretch;
	flex-direction: column;
`;

export const Parcours: NextPage<NextPageProps> = ({ seo, client }: NextPageProps) => {
	const screenHeight = useSreenHeight();
	const router = useRouter();
	const slug = router.query.slug as string;
	const directionProfile = router.query.directionProfile as DirectionProfiles || 'walking';
	const { data: parcours } = useRoute(slug, router.locale || 'fr');
	const { data: capsules } = useCapsulesFromRouteSlug(slug, router.locale || 'fr');
	const { default: map } = useMap();
	const {
		heightOffsetButtons, mapWalk, mapBike, mapCar, mapPath,
	} = MapStore.useState((s) => s);

	useEffect(() => {
		if (client) {
			MapStore.update((s) => {
				s.mapPath = !!client.attributes.mapPath;
				s.mapWalk = !!client.attributes.mapWalk;
				s.mapBike = !!client.attributes.mapBike;
				s.mapCar = !!client.attributes.mapCar;
			});
		}
	}, [client]);

	const onBack = useCallback(() => {
		router.push({
			pathname: '/map',
			query:    {
				directionProfile,
			},
		});
	}, [directionProfile, router]);

	useEffect(() => {
		MapStore.update((s) => {
			s.parcoursSelected = slug;
			s.capsuleSelected = null;
		});
	}, [slug]);

	useEffect(() => {
		if (capsules && map) {
			map.flyTo({
				center: [
					capsules[0].attributes.longitude,
					capsules[0].attributes.latitude,
				],
				zoom:    17,
				padding: {
					top:    0,
					bottom: Math.round(heightOffsetButtons / 2),
					left:   0,
					right:  0,
				},
			});
		}
	}, [capsules, heightOffsetButtons, map]);

	const points = useMemo(() => {
		const pointsValue: Array<Array<number>> = [];
		capsules?.forEach((capsule) => {
			pointsValue.push([capsule.attributes.longitude, capsule.attributes.latitude]);
		});
		return pointsValue;
	}, [capsules]);

	const { data: mapBoxDirectionsWalking } = useMapBoxDirections(mapWalk ? 'walking' : null, points);
	const { data: mapBoxDirectionsCycling } = useMapBoxDirections(mapBike ? 'cycling' : null, points);
	const { data: mapBoxDirectionsDriving } = useMapBoxDirections(mapCar ? 'driving' : null, points);

	const onDirectionProfile = useCallback((profile: DirectionProfiles) => {
		router.push({
			pathname: `/map/parcours/${slug}`,
			query:    {
				directionProfile: profile,
			},
		}, undefined, {
			shallow: true,
		});
	}, [slug, router]);

	return (
		<>
			<NextSeo {...seo} />
			<StyledContainer screenHeight={screenHeight}>
				<MapHeaderBackButton
					title={parcours?.attributes.title}
					subTitle={parcours?.attributes.description}
					onBack={onBack}
				/>

				<MapBox capsules={convertCapsulesToFeatures(capsules || [])}>
					<MapParcoursInfos
						routeSlug={slug}
						timeWalking={mapBoxDirectionsWalking?.duration}
						timeCycling={mapBoxDirectionsCycling?.duration}
						timeDriving={mapBoxDirectionsDriving?.duration}
						directionProfile={directionProfile}
						onDirectionProfile={onDirectionProfile}
					/>
					{ mapPath && capsules && capsules.length > 0 && <>
						<Directions
							profile={directionProfile}
							points={points}
							uniqueId={capsules.map((capsule) => capsule.attributes.slug).join('_')}
							key={capsules.map((capsule) => capsule.attributes.slug).join('_')}
						/>
					</>}
				</MapBox>

			</StyledContainer>
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async (
	context: GetServerSidePropsContext,
) => {
	const nextProps = await checkClient(context, ['map'], ['common']) as { props?: NextPageProps };

	if (!nextProps.props) {
		return nextProps as unknown as Promise<GetServerSidePropsResult<NextPageProps>>;
	}

	const { slug } = context.params as { slug: string };

	const parcours = await fetchRoute(slug, context.locale || 'fr');

	return {
		props: {
			...nextProps.props,

			seo: {
				...nextProps.props.seo,

				title:       parcours?.attributes.title,
				description: parcours?.attributes.description,

				openGraph: {
					type:        'website',
					title:       parcours?.attributes.title,
					description: parcours?.attributes.description,
					site_name:   nextProps.props.seo.openGraph.site_name,
					url:         nextProps.props.seo.openGraph.url,
					images:      nextProps.props.client.attributes.shareImage?.data ? [
						{
							url:    nextProps.props.client.attributes.shareImage.data.attributes.url,
							alt:    'Musair',
							width:  nextProps.props.client.attributes.shareImage.data.attributes.width,
							height: nextProps.props.client.attributes.shareImage.data.attributes.height,
						},
					] : [],
				},
			},
		},
	};
};

export default Parcours;
